import { AppAnswerDoc, AppAssistApi } from "./AppAssistApi";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppIcons } from "./AppIcons";

// AppExportRules - "Export chat (CSV)": the page's half of `/export`, as rules with no transport.
//
// ==================================================================================================
// !! WHY (owner-approved #8, 2026-09-27)
// ==================================================================================================
//
// The owner wants ONE contact's WHOLE conversation as a spreadsheet for their own records: every message,
// its date and time, who wrote it, what kind it was, its text, what it replied to and the reactions on it.
// The service builds the file (`Bridge/src/ExportRules.ts` + `ExportRun.ts`) from the same readers as the
// AI panel's transcript, and the page only asks for it and saves it (`AppExport.ts`, the browser half).
//
// ⛔ PURE - no DOM, no `window`, no `XMLHttpRequest`: the Bridge suite imports this file to pin the two
// halves together (`ExportContract.test.ts`), and a transport in here would drag the DOM into a Node
// typecheck (`AppAssistPanel`'s lesson).
//
// ⚠ A SUBSCRIBER'S OWN DATA, SO EVERY SUBSCRIBER MAY EXPORT - not owner-only like the AI panel. The proof
// is the `browser` archive key (`ArchiveKey.BrowserMatch`, service side); operator keys are refused.

/** What the service answered, read. `Ok` true = a CSV came back (`Rows` messages in it). */
export class AppExportAnswer {
    public Ok: boolean = false;
    public Why: string = "";
    public Rows: number = 0;
    /** ⭐ A refusal that clears on its own (`retry` seconds, 1-600; 2026-09-29 - the archive still being restored). 0 = final. */
    public RetrySec: number = 0;
}

export class AppExportRules {
    /** The route. ⛔ `ExportRules.Route` IS this constant - one string for both halves. */
    public static readonly Route: string = "/export";
    /** How many messages the file holds - read by the page for its "Done" line. Exposed by CORS. */
    public static readonly RowsHeader: string = "X-WAC-Rows";
    /** The answer's type when it is the file. Anything else is the JSON refusal every route gives. */
    public static readonly CsvType: string = "text/csv";
    /** ⭐ The answer's type when the page asked `format: "html"` (owner 2026-11-02: "Export chat as html instead of csv"). */
    public static readonly HtmlType: string = "text/html";
    /** The `format` value that asks for the HTML file. Absent (older pages) = the CSV, as before. */
    public static readonly HtmlFormat: string = "html";

    public static readonly ButtonId: string = "wac-export";
    public static readonly NoteId: string = "wac-export-note";
    public static readonly ButtonText: string = "Export chat";
    public static readonly BusyText: string = "Exporting…";
    /**
     * The button's words live in their own span, beside the Download icon: the busy state changes ONLY this span's
     * text (`AppExportJob.Busy`), so the icon survives the relabel (a `textContent` on the button itself dropped it).
     */
    public static readonly LabelClass: string = "xportlabel";

    /**
     * The request: the proof, the page (resolved by the service through the subscriber's OWN index), and
     * the time zone the file's Date/Time columns are written in. ⚠ `tz` is T1's field (`AppTimeZone.Here`, as on
     * `/folder` and `/assist`); the service checks it, falls back to the stored setting, then UTC, and the column
     * header says which it used. `sep` is `SepFor`'s answer.
     */
    public static Body = (Phone: string, Key: string, Page: string, Tz: string, Sep: string = ",",
                          Format: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Doc: Record<string, string> = { number: nz(Phone).trim(), key: nz(Key).trim(), page: nz(Page).trim(),
                tz: nz(Tz).trim(), sep: nz(Sep) };
            if (nz(Format).length > 0) { Doc.format = nz(Format); }
            return JSON.stringify(Doc);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportRules.Body");
            return "";
        }
    };

    /**
     * "<Name> - WhatsApp chat.csv". The name keeps its letters (Björn stays Björn) and its spaces; what a
     * file system refuses (`\ / : * ? " < > |` and control characters) goes, runs of spaces become one.
     * No name left = "WhatsApp chat.csv".
     */
    public static FileName = (Title: string, Ext: string = "csv"): string => {
        // REUSE-JUSTIFIED: kFileDownload.SafeName imports ReUsableTS/kalmstromShared4, which this
        // App does not load. This rule also compiles in the Node Bridge; its separator/cap policy differs.
        if (getCoreUtils().KErrorMode === true) { return "WhatsApp chat.csv"; }
        try {
            const Tail: string = "WhatsApp chat." + (Ext === "html" ? "html" : "csv");
            const Stem: string = nz(Title).replace(/[\\/:*?"<>|\u0000-\u001f\u007f]+/g, " ")
                .replace(/\s+/g, " ").trim().replace(/^[.\s]+|[.\s]+$/g, "").substring(0, 60).trim();
            if (Stem.length === 0) { return Tail; }
            return Stem + " - " + Tail;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportRules.FileName");
            return "WhatsApp chat.csv";
        }
    };

    /**
     * The column separator for this browser's locale: ";" where the decimal mark is "," (sv-SE, de-DE - where
     * Windows' list separator, which Excel splits a double-clicked CSV on, is ";"), else "," (en-US, en-GB).
     * Coordinator decision 2026-09-27. An empty or unknown locale is ICU's default; a failure is ",".
     */
    public static SepFor = (Locale: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ","; }
        try {
            let Tag: string | undefined = undefined;
            if (nz(Locale).trim().length > 0) { Tag = nz(Locale).trim(); }
            for (const Part of new Intl.NumberFormat(Tag).formatToParts(1.5)) {
                if (Part.type === "decimal" && Part.value === ",") { return ";"; }
            }
            return ",";
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: `Intl` throws RangeError for a malformed locale tag from the browser -
            // an EXPECTED outside value (fate 1); "," (RFC 4180) IS the defined answer.
            return ",";
        }
    };

    /** True when the answer's `Content-Type` is the file. DOCUMENTED EXCEPTION: pure string test.
     *  ERROR-SUPPRESSED-JUSTIFIED: false (read it as a refusal) is the safe answer. */
    public static IsCsv = (ContentType: string | null): boolean => {
        return nz(ContentType).trim().toLowerCase().indexOf(AppExportRules.CsvType) === 0;
    };

    /** True when the answer is the file - the CSV or the HTML. ERROR-SUPPRESSED-JUSTIFIED: pure string test, false = refusal. */
    public static IsFile = (ContentType: string | null): boolean => {
        return AppExportRules.IsCsv(ContentType) === true
            || nz(ContentType).trim().toLowerCase().indexOf(AppExportRules.HtmlType) === 0;
    };

    /**
     * Read what came back.
     *
     * ⛔ THE BODY IS READ WHATEVER THE STATUS (`AppSignUpApi.Answer`'s rule): refusals are sentences in a
     * 200. ⚠ STATUS 0 IS OUR END (`OursWhy`). ⚠ NO BOOLEAN `ok` IS A SERVICE WITHOUT THE ROUTE - an older
     * service answers an unknown path with its status document - `NotYetWhy`, never "at our end".
     */
    public static Parse = (Status: number, ContentType: string | null, JsonText: string, RowsText: string | null,
                           OursWhy: string): AppExportAnswer => {
        if (getCoreUtils().KErrorMode === true) { return AppExportRules.No(OursWhy); }
        try {
            if (Status === 200 && AppExportRules.IsFile(ContentType) === true) {
                const Out: AppExportAnswer = new AppExportAnswer();
                Out.Ok = true;
                const N: number = Math.floor(Number(nz(RowsText)));
                if (isNaN(N) === false && N >= 0) { Out.Rows = N; }
                return Out;
            }
            const Read: AppAnswerDoc = AppAssistApi.ReadAnswerDoc(Status, nz(JsonText));
            const Doc: Record<string, unknown> | null = Read.Doc;
            if (Doc === null) {
                const Gone: AppExportAnswer = AppExportRules.No(OursWhy);
                return Gone;
            }
            if (Read.HasOk === false) { return AppExportRules.No(AppExportRules.NotYetWhy()); }
            let Why: string = "";
            if (typeof Doc.why === "string") { Why = nz(Doc.why); }
            if (Why.length === 0) { Why = OursWhy; }
            const Refused: AppExportAnswer = AppExportRules.No(Why);
            Refused.RetrySec = AppAssistApi.RetryOf(Doc.retry);
            return Refused;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an answer that is not JSON is the service's or a proxy's - an
            // EXPECTED outside condition (fate 1): the page says it was our end, and the gate stays open.
            return AppExportRules.No(OursWhy);
        }
    };

    /** Quiet re-asks after `retry` refusals (15 s each at the service = 5 minutes); then the sentence, as before. */
    public static readonly MaxAgain: number = 20;

    /**
     * ⭐ How long to wait before asking again (ms), or 0 = show the answer. Only a refusal that carries `retry`, and only
     * while fewer than `MaxAgain` re-asks were made - the one decision `AppExportJob` acts on.
     */
    public static AgainAfterMs = (Answer: { Ok: boolean; RetrySec: number }, Tries: number,
                                  MaxAgain: number = AppExportRules.MaxAgain): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (Answer.Ok === true || Answer.RetrySec <= 0) { return 0; }
            if (Tries >= MaxAgain) { return 0; }
            return Answer.RetrySec * 1000;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportRules.AgainAfterMs");
            return 0;
        }
    };

    /** The note while the service is still bringing the archive back after a restart. DOCUMENTED EXCEPTION: constant. */
    public static ReadyingNote = (): string => {
        return "Getting your archive ready…";
    };

    /** DOCUMENTED EXCEPTION: builds a refusal value. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static No = (Why: string): AppExportAnswer => {
        const Out: AppExportAnswer = new AppExportAnswer();
        Out.Why = nz(Why);
        return Out;
    };

    // ── What the page says while it works and when it is done (law 7: never a silent wait) ──────────

    /**
     * The line under the button, ticked at least once a second: seconds while the service prepares the
     * file, then how much has arrived. DOCUMENTED EXCEPTION: pure formatting of numbers in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: called only from the guarded job.
     */
    public static Working = (Seconds: number, Bytes: number): string => {
        let Secs: number = Math.floor(Seconds);
        if (isNaN(Secs) === true || Secs < 0) { Secs = 0; }
        if (Bytes > 0) { return "Downloading… " + AppExportRules.Size(Bytes) + " (" + String(Secs) + " s)"; }
        return "Preparing the file… " + String(Secs) + " s";
    };

    /** "Saved "Anna - WhatsApp chat.csv" - 20,029 messages. See your downloads." */
    public static Done = (Name: string, Rows: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Count: string = String(Math.max(0, Math.floor(Rows))).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            Count = Count + " message";
            if (Rows !== 1) { Count = Count + "s"; }
            return "Saved \"" + nz(Name) + "\" - " + Count + ". See your downloads.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportRules.Done");
            return "";
        }
    };

    /** "812 KB" / "1.4 MB". DOCUMENTED EXCEPTION: pure formatting. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static Size = (Bytes: number): string => {
        // REUSE-JUSTIFIED: kByteSize.FormatBytes imports ReUsableTS/kalmstromShared4, unavailable to
        // this App and the Bridge rules compilation, and uses locale/units unlike this fixed KB/MB wire UI.
        if (!(Bytes >= 1048576)) { return String(Math.max(1, Math.round(Bytes / 1024))) + " KB"; }
        return (Math.round(Bytes / 104857.6) / 10).toFixed(1) + " MB";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static NotYetWhy = (): string => {
        return "Exporting chats isn't available yet.";
    };

    /** ⚠ The browser cannot save files from this page (no Blob / download support). A sentence the owner can
     *  act on (another browser), not a fault. DOCUMENTED EXCEPTION: pure constant sentence. */
    public static CannotSaveWhy = (): string => {
        return "This browser cannot save the file. Try again in another browser, such as Chrome, Edge, Firefox "
            + "or Safari.";
    };

    // ── The control ───────────────────────────────────────────────────────────────────────────────

    /**
     * The button and its note, for the line above the chat card. "" when the page cannot export (no
     * service, no key, a group). ⚠ Constant markup: no contact text goes in, so nothing needs escaping.
     */
    public static Control = (On: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (On !== true) { return ""; }
            return "<span class=\"xport\"><span class=\"xportnote\" id=\"" + AppExportRules.NoteId
                + "\" role=\"status\" aria-live=\"polite\"></span>" + AppExportRules.Button("btn ghost sm", "") + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportRules.Control");
            return "";
        }
    };

    /**
     * The chat header's split of `Control` (design item 9, 2026-09-30): the SAME button, as an item of the "⋯" menu, and
     * the SAME note, outside the menu so the progress and "Saved ..." stay in view after the menu shuts. Ids unchanged.
     */
    public static MenuButton = (On: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (On !== true) { return ""; }
            return AppExportRules.Button("menuitem", " role=\"menuitem\"");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportRules.MenuButton");
            return "";
        }
    };

    /** The one export button's markup: `xportbtn` + `Cls`, `Extra` attributes (a menu role), the Download icon and its label. */
    private static Button = (Cls: string, Extra: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<button type=\"button\" class=\"xportbtn " + Cls + "\"" + Extra + " id=\"" + AppExportRules.ButtonId
                + "\" title=\"Download this whole conversation as a web page file\">" + AppIcons.Svg(AppIcons.Download, 16)
                + "<span class=\"" + AppExportRules.LabelClass + "\">" + AppExportRules.ButtonText + "</span></button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportRules.Button");
            return "";
        }
    };

    public static Note = (On: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (On !== true) { return ""; }
            return "<span class=\"xportnote\" id=\"" + AppExportRules.NoteId + "\" role=\"status\" aria-live=\"polite\"></span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportRules.Note");
            return "";
        }
    };
}
